import type { World } from "@iyk/music-domain/client"
import * as UI from "@iyk/music-ui"
import { AppLink, useForm, type AppRoute } from "@iyk/react-router/client"
import { Match } from "effect"
import { AppRoutes } from "../../../AppRoutes.ts"
import { WorldAvatar } from "../../../worlds/ui/WorldAvatar.tsx"
import type { UiUnlock } from "../../UiUnlock.ts"

type RootProps = {
  children: React.ReactNode
  policy: UiUnlock.Policy
  world: UiUnlock.World
  open?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

const Root = ({ children, policy, world, open, onOpenChange }: RootProps) => {
  return (
    <UI.Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
      <UI.Dialog.Content>
        {Match.valueTags(policy, {
          AllFans: () => <AllFansContent world={world} />,
          SpecificFans: ({ conditions }) => <SpecificFansContent conditions={conditions} world={world} />
        })}
      </UI.Dialog.Content>
    </UI.Dialog.Root>
  )
}

const AllFansContent = ({ world }: { world: { avatarUrl: string; name: string; slug: World.Slug } }) => {
  const { Form, isSubmitting } = useForm()

  return (
    <div className="flex flex-col items-center justify-center gap-6 pt-0 sm:pt-4 pb-4">
      <div className="flex flex-col gap-1 text-sm text-center">
        <p>Required for access</p>
        <p className="text-gray-11">Join {world.name}'s world</p>
      </div>
      <WorldAvatar url={world.avatarUrl} name={world.name} size="lg" />
      <Form method="post" action={AppRoutes.worlds.home(world.slug)}>
        <input type="hidden" name="intent" value="Join" />
        <UI.Button isLoading={isSubmitting} kind="solid" size="md" type="submit">
          Join
        </UI.Button>
      </Form>
    </div>
  )
}

interface SpecificFansContentProps {
  conditions: Array<UiUnlock.Policy.SpecificFans.Condition>
  world: UiUnlock.World
}

const SpecificFansContent = ({ conditions, world }: SpecificFansContentProps) => {
  return (
    <div className="flex flex-col gap-10 pt-2 pb-2 sm:pt-4 sm:pb-0">
      <div className="flex flex-col items-center justify-center gap-6">
        <UI.Card padding="sm" rounded="md">
          <div className="flex items-center justify-center gap-1">
            <UI.Icon.Proof className="size-4" />
            <div className="text-sm-departure">1</div>
          </div>
        </UI.Card>

        <div className="flex flex-col gap-1 text-sm text-center">
          <p>Proofs required for access</p>
          <p className="text-gray-11">Any of the following</p>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {conditions.map((condition, index) =>
          Match.valueTags(condition, {
            ItemClaim: ({ items }) => (
              <ConditionGroup key={`condition-${index}`} label="Item claims:">
                {items.map((item) => (
                  <ConditionRow
                    key={item.id}
                    to={AppRoutes.worlds.items.detail(world.slug, item.id)}
                    imageUrl={item.primaryMedia.fileUrl}
                    name={item.name}
                  />
                ))}
              </ConditionGroup>
            ),
            ShowCheckIn: ({ shows }) => (
              <ConditionGroup key={`condition-${index}`} label="Show check-ins:">
                {shows.map((show) => (
                  <ConditionRow
                    key={show.id}
                    to={AppRoutes.worlds.shows.detail(world.slug, show.id)}
                    imageUrl={show.proofImage.fileUrl}
                    name={show.name}
                  />
                ))}
              </ConditionGroup>
            )
          })
        )}
      </div>
    </div>
  )
}

type ConditionRowProps = {
  to: AppRoute
  imageUrl: string
  name: string
}

function ConditionRow({ to, imageUrl, name }: ConditionRowProps) {
  return (
    <AppLink
      to={to}
      className="flex items-center gap-3 border-b border-grayA-2 py-2 first:pt-0 last:pb-0 last:border-b-0"
    >
      <img src={imageUrl} alt={name} className="size-7 object-cover rounded-md" />
      <span className="text-sm">{name}</span>
    </AppLink>
  )
}

function ConditionGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-gray-11">{label}</p>
      <div className="p-3 bg-gray-2 border border-grayA-2 rounded-lg">{children}</div>
    </div>
  )
}

export const UnlockPolicyDialog = {
  Root,
  Trigger: UI.Dialog.Trigger
}
